import { useEffect } from "react";
import { ExampleSchematic } from "@/components/landing/example-schematic";
import { exampleSimulateState } from "@/lib/example-simulate-state";
import { useSystemStore } from "@/lib/store";
import type { System } from "@/lib/types";
import { TOOL_EXAMPLES, type ToolExampleId } from "@/lib/tool-example-systems";

/** Mounted in its own frame so every example has an independent scenario. */
export function WiringSchematic({ system, embedded = true, tool, initialMode = "simulate", library = false }: { system: System; embedded?: boolean; tool?: ToolExampleId; initialMode?: "edit" | "simulate"; library?: boolean }) {
  const loadSystem = useSystemStore((s) => s.loadSystem);
  const mode = useSystemStore((s) => s.mode);
  useEffect(() => {
    if (library && window.parent !== window) {
      window.parent.postMessage({ type: "crimp-example-mode", mode }, window.location.origin);
    }
  }, [library, mode]);
  const example = tool ? TOOL_EXAMPLES[tool] : null;
  useEffect(() => {
    const { mode, simulateState } = useSystemStore.getState();
    loadSystem(system, { preserveLayout: true });
    useSystemStore.setState({ mode: tool ? "edit" : initialMode, simulateState: exampleSimulateState(system) });
    return () => useSystemStore.setState({ mode, simulateState });
  }, [loadSystem, system, tool, initialMode]);

  return <ExampleSchematic layout={library ? "library" : "resource"} embedded={embedded}
    allowSimulation={!tool} showCableDetails={!!tool || initialMode === "edit"}
    ariaLabel={`${system.name}. Pan and zoom to explore; hover or tap a cable for its size and fuse.`}
    caption={library ? <span className="font-semibold text-[var(--lp-ink)]">Explore the wiring</span> : example || initialMode === "edit" ? <><span className="font-semibold text-[var(--lp-ink)]">{example ? "Worked schematic." : "Interactive wiring."}</span>{" "}Pan and zoom; select a cable to inspect its calculation.</> : <><span className="font-semibold text-[var(--lp-ink)]">A live example.</span>{" "}Switch equipment on and off to see power and runtime change.</>}
    simulationHint="Switch equipment on or off in the drawing, or adjust battery charge to see power and runtime change."
    wiringResult={example ? <div className="space-y-5 p-5 text-[14px] leading-relaxed text-[var(--lp-ink-2)]">
      {example.points.map(point => <div key={point.title}><p className="font-semibold text-[var(--lp-ink)]">{point.title}</p><p className="mt-2">{point.body}</p></div>)}
      <p className="border-t border-[var(--lp-rule)] pt-4 text-[13px]">{example.assumptions}</p>
    </div> : <div className="space-y-4 p-5 text-[14px] leading-relaxed text-[var(--lp-ink-2)]">
      <p className="font-semibold text-[var(--lp-ink)]">Explore the wiring</p>
      <p>Pan and zoom to follow each circuit. Hover or tap a cable to see its wire size, fuse and voltage drop.</p>
      <p>Wiring mode shows the design loads; Simulate lets you try different equipment states.</p>
    </div>}
  />;
}
